import React from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import { BentoProvider } from '../../Provider';

type Measure = {
    width: number;
    height: number;
};

const FakeWrap = styled.div`
    & * {
        position: initial !important;
    }
`;

/**
 *
 * @param element The element you want to measure
 * @param measureCallback Called with a "Measurement" object. CAUTION: this helper renders children without i18n context. Sizing may be different when "element" is translated!
 */
export const measureElement = (element: React.ReactNode, measureCallback: (m: Measure) => void) => {
    // Creates the hidden div appended to the document body
    const container = document.createElement('div');
    container.style.display = 'inline-block';
    container.style.position = 'absolute';
    container.style.visibility = 'hidden';
    container.style.zIndex = '-1';
    document.body.appendChild(container);

    // Renders the React element into the hidden div
    ReactDOM.render(
        <FakeWrap
            ref={el => {
                if (el) {
                    measureCallback({ width: el.offsetWidth, height: el.offsetHeight });
                }
            }}
        >
            <BentoProvider>{element}</BentoProvider>
        </FakeWrap>,
        container,
        () => {
            ReactDOM.unmountComponentAtNode(container);
            container.parentNode?.removeChild(container);
        },
    );
};

const MeasureHelperWrapper = styled.div`
    display: inline-block;
    z-index: -1;
    position: absolute;
    opacity: 0;
    visibility: hidden;

    & * {
        position: initial !important;
    }
`;

export const MeasureHelper: React.FC<{ onMeasure: (m: Measure) => void }> = props => {
    const measureHelperRef = React.useRef<HTMLDivElement>(null);
    const [renderMeasureHelper, setRenderMeasureHelper] = React.useState<boolean>(true);

    React.useEffect(() => {
        const onResize = () => {
            setRenderMeasureHelper(true);
        };

        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    React.useEffect(() => {
        if (renderMeasureHelper) {
            if (measureHelperRef.current) {
                props.onMeasure({
                    width: measureHelperRef.current.offsetWidth,
                    height: measureHelperRef.current.offsetHeight,
                });
            }
            setRenderMeasureHelper(false);
        }
    }, [renderMeasureHelper]);

    return renderMeasureHelper ? (
        <MeasureHelperWrapper ref={measureHelperRef}>{props.children}</MeasureHelperWrapper>
    ) : null;
};

export default measureElement;
